import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ref, onValue, set, update, get } from 'firebase/database';
import { database } from './firebase-config';
import { v4 as uuidv4 } from 'uuid';
import { VESSEL_DATA } from './types/vessel';
import { NavigationState, NavLogEntry, defaultNavigationState } from './types/navigation-types';

// Firebase Realtime Database rejects `undefined` values. Nav state and log
// entries legitimately omit optional fields (heading, destinationHex, hex coords
// before the grid is seeded, etc.), so strip undefined recursively before any
// write reaches Firebase.
function stripUndefined<T>(value: T): T {
  if (Array.isArray(value)) return value.map((v) => stripUndefined(v)) as unknown as T;
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (v !== undefined) out[k] = stripUndefined(v);
    }
    return out as T;
  }
  return value;
}

// Exports taken before the clock/role system was removed carry provisions inside
// a `resources` object and count time in periods. Lift the two values that still
// mean something and drop the rest on the floor; unknown keys are harmless
// because the seed merges over the current defaults.
function migrateLegacyGameState(
  imported?: Record<string, any>
): Record<string, any> {
  if (!imported) return {};

  const migrated = { ...imported };

  if (migrated.provisions === undefined && typeof migrated.resources?.provisions === 'number') {
    migrated.provisions = migrated.resources.provisions;
  }
  if (migrated.voyageDay === undefined && typeof migrated.currentPeriod === 'number') {
    migrated.voyageDay = migrated.currentPeriod;
  }

  for (const dead of [
    'clocks', 'clockHistory', 'roleAssignments', 'resources', 'roleAbilityUsage',
    'playerSelectedRoles', 'currentPeriod', 'timeScale', 'estimatedVoyageWeeks',
    'periodTimeScales', 'periodWorkflow'
  ]) {
    delete migrated[dead];
  }

  // The old shape carried morale/cleanliness counters alongside navigation.
  if (migrated.consecutiveFailures) {
    migrated.consecutiveFailures = {
      navigation: migrated.consecutiveFailures.navigation ?? 0
    };
  }

  return migrated;
}

// Define fleet ship structure for DM tracking
export interface FleetShip {
  id: string;
  name: string;
  vesselType: string;
  deckSections: {
    upper: Array<{
      current: number;
      max: number;
      hasDealtHoleDamage?: boolean;
    }>;
    middle?: Array<{
      current: number;
      max: number;
      hasDealtHoleDamage?: boolean;
    }>;
    lower?: Array<{
      current: number;
      max: number;
      hasDealtHoleDamage?: boolean;
    }>;
    [key: `middle${number}`]: Array<{
      current: number;
      max: number;
      hasDealtHoleDamage?: boolean;
    }>;
  };
  riggingSections: Array<{
    current: number;
    max: number;
  }>;
}

// Define the shape of our session data
interface SessionData {
  id: string;
  createdAt: number;
  lastUpdated: number;
  activeUsers: Record<string, {
    name: string;
    lastActive: number;
    role: 'DM' | 'Player';
  }>;
  gameState: {
    weatherData: {
      climate: string;
      temperature: string;
      wind: string;
      windStrength: string;
      windDirection: string;
      precipitation: string;
    };
    selectedVessel: string;
    deckSections: {
      upper: Array<{
        current: number;
        max: number;
        hasDealtHoleDamage?: boolean;
      }>;
      middle?: Array<{
        current: number;
        max: number;
        hasDealtHoleDamage?: boolean;
      }>;
      lower?: Array<{
        current: number;
        max: number;
        hasDealtHoleDamage?: boolean;
      }>;
    };
    riggingSections: Array<{
      current: number;
      max: number;
    }>;
    dmFleet: FleetShip[];
    playerFleet: FleetShip[];

    // Crew and provisions
    /** Souls aboard. Seeded from the vessel's Stormwrack complement, DM-editable. */
    crewSize: number;
    /** Provision points. One point feeds one crew member for one day. */
    provisions: number;
    /** Days elapsed since the voyage began. Also stamps navigation log entries. */
    voyageDay: number;
    /** Who makes the daily Knowledge (geography) check, for the nav log. */
    navigatorName: string;

    voyageActive: boolean;
    voyageDestination?: {
      name: string;
      distance: 'close' | 'distant';
      familiarity: 'very familiar' | 'studied carefully' | 'seen once' | 'uncertain' | 'mythical or legendary';
      startPointKnowledge: 'well known' | 'uncertain' | 'only guessed at' | 'completely unknown';
      chartQuality: 'none' | 'excellent' | 'poor' | 'extremely poor' | 'false';
      courseSettingDC?: number;
      courseSettingResult?: {
        roll: number;
        totalBonus: number;
        total: number;
        success: boolean;
        missedBy: number;
        missDistance?: string;
      };
    };
    /** Consecutive failed navigation checks. Two in a row puts the ship off course. */
    consecutiveFailures?: {
      navigation: number;
    };

    // Navigation / Dead-Reckoning System
    navigation: NavigationState;
    navLog: NavLogEntry[];
  };
}

// Default state for a new session
const defaultGameState = {
  weatherData: {
    climate: 'Temperate',
    temperature: '',
    wind: '',
    windStrength: '',
    windDirection: '',
    precipitation: '',
  },
  selectedVessel: 'Caravel',
  deckSections: {
    upper: Array.from({ length: VESSEL_DATA['Caravel'].sectionsPerDeck }, () => ({
      current: VESSEL_DATA['Caravel'].sectionHP,
      max: VESSEL_DATA['Caravel'].sectionHP,
      hasDealtHoleDamage: false
    })),
    middle: VESSEL_DATA['Caravel'].decks > 2 ?
      Array.from({ length: VESSEL_DATA['Caravel'].sectionsPerDeck }, () => ({
        current: VESSEL_DATA['Caravel'].sectionHP,
        max: VESSEL_DATA['Caravel'].sectionHP,
        hasDealtHoleDamage: false
      })) : [],
    lower: VESSEL_DATA['Caravel'].decks > 1 ?
      Array.from({ length: VESSEL_DATA['Caravel'].sectionsPerDeck }, () => ({
        current: VESSEL_DATA['Caravel'].sectionHP,
        max: VESSEL_DATA['Caravel'].sectionHP,
        hasDealtHoleDamage: false
      })) : []
  },
  riggingSections: Array.from({ length: VESSEL_DATA['Caravel'].riggingSections }, () => ({
    current: VESSEL_DATA['Caravel'].riggingHP,
    max: VESSEL_DATA['Caravel'].riggingHP
  })),
  dmFleet: [],
  playerFleet: [],

  // Crew and provisions defaults (seeded from the default vessel's complement)
  crewSize: VESSEL_DATA['Caravel'].complement,
  provisions: VESSEL_DATA['Caravel'].complement * 14, // two weeks' rations
  voyageDay: 0,
  navigatorName: 'The navigator',

  voyageActive: false,
  consecutiveFailures: {
    navigation: 0
  },

  // Navigation / Dead-Reckoning System defaults
  navigation: defaultNavigationState,
  navLog: []
};

interface SessionContextType {
  sessionId: string | null;
  userId: string;
  userName: string;
  setUserName: (name: string) => void;
  gameState: SessionData['gameState'] | null;
  createSession: (asDM: boolean, initialGameState?: Partial<SessionData['gameState']>) => Promise<string>;
  joinSession: (id: string, asDM: boolean) => Promise<boolean>;
  leaveSession: () => void;
  updateGameState: <K extends keyof SessionData['gameState']>(
    key: K,
    value: SessionData['gameState'][K]
  ) => Promise<void>;
  activeUsers: Record<string, {
    name: string;
    lastActive: number;
    role: 'DM' | 'Player'
  }>;
  isDM: () => boolean;
  canClaimDM: () => boolean;
  claimDMRole: () => Promise<boolean>;
  releaseDMRole: () => Promise<void>;
  addFleetShip: (name: string, vesselType: string) => Promise<void>;
  updateFleetShip: (id: string, ship: FleetShip) => Promise<void>;
  removeFleetShip: (id: string) => Promise<void>;
  addPlayerFleetShip: (name: string, vesselType: string) => Promise<void>;
  updatePlayerFleetShip: (id: string, ship: FleetShip) => Promise<void>;
  removePlayerFleetShip: (id: string) => Promise<void>;

  // Voyage methods
  /** Advance one day: bumps the day counter and feeds the crew. */
  advanceDay: () => Promise<{ day: number; provisions: number; shortfall: number }>;
  startVoyage: () => Promise<void>;
  endVoyage: () => Promise<void>;

  // Navigation / Dead-Reckoning System methods
  updateNavigation: (nav: NavigationState) => Promise<void>;
  addNavLogEntry: (entry: NavLogEntry) => Promise<void>;
  /**
   * Append several log entries in one write. A single resolved day can produce
   * two rows (a drift plus the check that followed it) and calling
   * addNavLogEntry in a loop drops all but the last — each await re-reads the
   * same stale navLog from this render's closure.
   */
  addNavLogEntries: (entries: NavLogEntry[]) => Promise<void>;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export const SessionProvider = ({ children }: { children: React.ReactNode }) => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [userId] = useState<string>(() => {
    const storedId = localStorage.getItem('userId');
    if (storedId) return storedId;
    const newId = uuidv4();
    localStorage.setItem('userId', newId);
    return newId;
  });
  const [userName, setUserName] = useState<string>(() => {
    return localStorage.getItem('userName') || 'Anonymous';
  });
  const [gameState, setGameState] = useState<SessionData['gameState'] | null>(null);
  const [activeUsers, setActiveUsers] = useState<Record<string, { name: string; lastActive: number; role: 'DM' | 'Player' }>>({});

  const navigate = useNavigate();
  const location = useLocation();

  // Keep the latest userName / role in refs so the presence effect can read them
  // without listing them as dependencies (which would tear down and recreate the
  // heartbeat + listener on every change — and, because each write updates
  // `activeUsers`, spin into a continuous write loop).
  const userNameRef = useRef(userName);
  userNameRef.current = userName;
  const roleRef = useRef<'DM' | 'Player'>('Player');
  roleRef.current = activeUsers[userId]?.role || 'Player';

  // Update local storage when userName changes
  useEffect(() => {
    localStorage.setItem('userName', userName);
  }, [userName]);

  // Handle user presence and session cleanup.
  // Depends only on session identity so the heartbeat/listener are set up once
  // per session, not recreated on every activeUsers update.
  useEffect(() => {
    if (!sessionId || !userId) return;

    // Reference to the current user's presence in this session
    const userPresenceRef = ref(database, `sessions/${sessionId}/activeUsers/${userId}`);

    // Set the initial presence
    set(userPresenceRef, {
      name: userNameRef.current,
      lastActive: Date.now(),
      role: roleRef.current
    });

    // Set up a presence system with a heartbeat
    const heartbeatInterval = setInterval(() => {
      update(userPresenceRef, { lastActive: Date.now() });
    }, 30000); // Update every 30 seconds

    // Listen for active users changes
    const activeUsersRef = ref(database, `sessions/${sessionId}/activeUsers`);
    const unsubscribeActiveUsers = onValue(activeUsersRef, (snapshot) => {
      const data = snapshot.val() || {};
      setActiveUsers(data);

      // Clean up inactive users (>5 minutes without updates)
      const now = Date.now();
      const inactiveTimeout = 5 * 60 * 1000; // 5 minutes

      Object.entries(data).forEach(([uid, userData]) => {
        const user = userData as { lastActive: number };
        if (now - user.lastActive > inactiveTimeout) {
          // Remove inactive user
          set(ref(database, `sessions/${sessionId}/activeUsers/${uid}`), null);
        }
      });
    });

    // Clean up on unmount
    return () => {
      clearInterval(heartbeatInterval);
      unsubscribeActiveUsers();

      // Only remove presence if we're actually leaving the session
      // (not just if the component is unmounting temporarily)
      if (location.pathname !== `/session/${sessionId}`) {
        set(userPresenceRef, null);
      }
    };
  }, [sessionId, userId, location.pathname]);

  // Push userName changes to the presence record without recreating the heartbeat.
  useEffect(() => {
    if (!sessionId || !userId) return;
    update(ref(database, `sessions/${sessionId}/activeUsers/${userId}`), {
      name: userName
    });
  }, [userName, sessionId, userId]);

  // Listen for game state changes when session ID changes
  useEffect(() => {
    if (!sessionId) {
      setGameState(null);
      return;
    }

    const gameStateRef = ref(database, `sessions/${sessionId}/gameState`);
    const unsubscribe = onValue(gameStateRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setGameState(data);
      }
    });

    return () => unsubscribe();
  }, [sessionId]);

  // Create a new session
  const createSession = async (
    asDM: boolean = false,
    initialGameState?: Partial<SessionData['gameState']>
  ): Promise<string> => {
    const newSessionId = uuidv4();

    // Shallow-merge any imported state over the defaults so that keys added by a
    // newer app version still receive sane defaults when seeding from an older
    // export. stripUndefined guards against Firebase rejecting undefined values.
    const seededGameState = stripUndefined({
      ...defaultGameState,
      ...migrateLegacyGameState(initialGameState)
    });

    const sessionData: SessionData = {
      id: newSessionId,
      createdAt: Date.now(),
      lastUpdated: Date.now(),
      activeUsers: {
        [userId]: {
          name: userName,
          lastActive: Date.now(),
          role: asDM ? 'DM' : 'Player'
        }
      },
      gameState: seededGameState
    };

    await set(ref(database, `sessions/${newSessionId}`), sessionData);
    setSessionId(newSessionId);
    navigate(`/session/${newSessionId}`);
    return newSessionId;
  };

  // Join an existing session
  const joinSession = async (id: string, asDM: boolean = false): Promise<boolean> => {
    const sessionRef = ref(database, `sessions/${id}`);
    const snapshot = await get(sessionRef);

    if (!snapshot.exists()) {
      return false;
    }

    const sessionData = snapshot.val();
    const hasDM = Object.values(sessionData.activeUsers || {}).some(
      (user: any) => user.role === 'DM'
    );

    // Don't allow multiple DMs unless the current DM is inactive
    const finalRole = asDM && hasDM ? 'Player' : (asDM ? 'DM' : 'Player');

    // Update active users
    await update(ref(database, `sessions/${id}/activeUsers/${userId}`), {
      name: userName,
      lastActive: Date.now(),
      role: finalRole
    });

    // Update last updated timestamp
    await update(ref(database, `sessions/${id}`), {
      lastUpdated: Date.now()
    });

    setSessionId(id);
    navigate(`/session/${id}`);
    return true;
  };

  // Leave the current session
  const leaveSession = () => {
    if (sessionId) {
      // Remove user from active users
      set(ref(database, `sessions/${sessionId}/activeUsers/${userId}`), null);
    }

    setSessionId(null);
    setGameState(null);
    navigate('/');
  };

  // Update a specific part of the game state
  const updateGameState = async <K extends keyof SessionData['gameState']>(
    key: K,
    value: SessionData['gameState'][K]
  ): Promise<void> => {
    if (!sessionId) return;

    await set(ref(database, `sessions/${sessionId}/gameState/${key}`), value);
    await update(ref(database, `sessions/${sessionId}`), {
      lastUpdated: Date.now()
    });
  };

  // Check if the current user is the DM
  const isDM = (): boolean => {
    return activeUsers[userId]?.role === 'DM';
  };

  // Check if the current user can claim the DM role
  const canClaimDM = (): boolean => {
    if (!sessionId) return false;

    // Can claim if there's no current DM
    return !Object.entries(activeUsers).some(([id, user]) =>
      id !== userId && user.role === 'DM'
    );
  };

  // Claim the DM role if available
  const claimDMRole = async (): Promise<boolean> => {
    if (!sessionId || !canClaimDM()) return false;

    try {
      await update(ref(database, `sessions/${sessionId}/activeUsers/${userId}`), {
        role: 'DM'
      });
      return true;
    } catch (error) {
      console.error('Error claiming DM role:', error);
      return false;
    }
  };

  // Release the DM role
  const releaseDMRole = async (): Promise<void> => {
    if (!sessionId || !isDM()) return;

    try {
      await update(ref(database, `sessions/${sessionId}/activeUsers/${userId}`), {
        role: 'Player'
      });
    } catch (error) {
      console.error('Error releasing DM role:', error);
    }
  };

  // Add a ship to the DM fleet
  const addFleetShip = async (name: string, vesselType: string): Promise<void> => {
    if (!sessionId || !isDM()) return;

    const vessel = VESSEL_DATA[vesselType];
    const newShip: FleetShip = {
      id: uuidv4(),
      name,
      vesselType,
      deckSections: {
        upper: Array.from({ length: vessel.sectionsPerDeck }, () => ({
          current: vessel.sectionHP,
          max: vessel.sectionHP,
          hasDealtHoleDamage: false
        }))
      },
      riggingSections: Array.from({ length: vessel.riggingSections }, () => ({
        current: vessel.riggingHP,
        max: vessel.riggingHP
      }))
    };

    // Add middle decks if applicable
    if (vessel.decks === 2) {
      newShip.deckSections.lower = Array.from({ length: vessel.sectionsPerDeck }, () => ({
        current: vessel.sectionHP,
        max: vessel.sectionHP,
        hasDealtHoleDamage: false
      }));
    } else if (vessel.decks >= 3) {
      for (let i = 1; i < vessel.decks - 1; i++) {
        newShip.deckSections[`middle${i}`] = Array.from({ length: vessel.sectionsPerDeck }, () => ({
          current: vessel.sectionHP,
          max: vessel.sectionHP,
          hasDealtHoleDamage: false
        }));
      }
      newShip.deckSections.lower = Array.from({ length: vessel.sectionsPerDeck }, () => ({
        current: vessel.sectionHP,
        max: vessel.sectionHP,
        hasDealtHoleDamage: false
      }));
    }

    const currentFleet = gameState?.dmFleet || [];
    await updateGameState('dmFleet', [...currentFleet, newShip]);
  };

  // Update a ship in the DM fleet
  const updateFleetShip = async (id: string, ship: FleetShip): Promise<void> => {
    if (!sessionId || !isDM()) return;

    const currentFleet = gameState?.dmFleet || [];
    const updatedFleet = currentFleet.map(s => s.id === id ? ship : s);
    await updateGameState('dmFleet', updatedFleet);
  };

  // Remove a ship from the DM fleet
  const removeFleetShip = async (id: string): Promise<void> => {
    if (!sessionId || !isDM()) return;

    const currentFleet = gameState?.dmFleet || [];
    const updatedFleet = currentFleet.filter(s => s.id !== id);
    await updateGameState('dmFleet', updatedFleet);
  };

  // Add a ship to the Player fleet
  const addPlayerFleetShip = async (name: string, vesselType: string): Promise<void> => {
    if (!sessionId) return;

    const vessel = VESSEL_DATA[vesselType];
    const newShip: FleetShip = {
      id: uuidv4(),
      name,
      vesselType,
      deckSections: {
        upper: Array.from({ length: vessel.sectionsPerDeck }, () => ({
          current: vessel.sectionHP,
          max: vessel.sectionHP,
          hasDealtHoleDamage: false
        }))
      },
      riggingSections: Array.from({ length: vessel.riggingSections }, () => ({
        current: vessel.riggingHP,
        max: vessel.riggingHP
      }))
    };

    // Add middle decks if applicable
    if (vessel.decks === 2) {
      newShip.deckSections.lower = Array.from({ length: vessel.sectionsPerDeck }, () => ({
        current: vessel.sectionHP,
        max: vessel.sectionHP,
        hasDealtHoleDamage: false
      }));
    } else if (vessel.decks >= 3) {
      for (let i = 1; i < vessel.decks - 1; i++) {
        newShip.deckSections[`middle${i}`] = Array.from({ length: vessel.sectionsPerDeck }, () => ({
          current: vessel.sectionHP,
          max: vessel.sectionHP,
          hasDealtHoleDamage: false
        }));
      }
      newShip.deckSections.lower = Array.from({ length: vessel.sectionsPerDeck }, () => ({
        current: vessel.sectionHP,
        max: vessel.sectionHP,
        hasDealtHoleDamage: false
      }));
    }

    const currentFleet = gameState?.playerFleet || [];
    await updateGameState('playerFleet', [...currentFleet, newShip]);
  };

  // Update a ship in the Player fleet
  const updatePlayerFleetShip = async (id: string, ship: FleetShip): Promise<void> => {
    if (!sessionId) return;

    const currentFleet = gameState?.playerFleet || [];
    const updatedFleet = currentFleet.map(s => s.id === id ? ship : s);
    await updateGameState('playerFleet', updatedFleet);
  };

  // Remove a ship from the Player fleet
  const removePlayerFleetShip = async (id: string): Promise<void> => {
    if (!sessionId) return;

    const currentFleet = gameState?.playerFleet || [];
    const updatedFleet = currentFleet.filter(s => s.id !== id);
    await updateGameState('playerFleet', updatedFleet);
  };

  // Voyage methods

  // Advance the voyage by one day and feed the crew. Provisions floor at zero;
  // the shortfall is returned rather than acted on, so the DM decides what
  // going hungry costs this crew.
  const advanceDay = async (): Promise<{ day: number; provisions: number; shortfall: number }> => {
    const crewSize = gameState?.crewSize ?? 0;
    const provisions = gameState?.provisions ?? 0;

    const day = (gameState?.voyageDay ?? 0) + 1;
    const shortfall = Math.max(0, crewSize - provisions);
    const remaining = Math.max(0, provisions - crewSize);

    await updateGameState('voyageDay', day);
    await updateGameState('provisions', remaining);

    return { day, provisions: remaining, shortfall };
  };

  // Start a new voyage
  const startVoyage = async (): Promise<void> => {
    await updateGameState('voyageActive', true);
  };

  // End the current voyage
  const endVoyage = async (): Promise<void> => {
    await updateGameState('voyageActive', false);
    // Clear voyage destination data from previous voyage
    await updateGameState('voyageDestination', null as any);
    await updateGameState('voyageDay', 0);
    // Reset consecutive failures
    await updateGameState('consecutiveFailures', { navigation: 0 });
    // Reset navigation state and clear the dead-reckoning log
    await updateGameState('navigation', defaultNavigationState);
    await updateGameState('navLog', []);
  };

  // Navigation / Dead-Reckoning System methods

  // Replace the whole navigation state object
  const updateNavigation = async (nav: NavigationState): Promise<void> => {
    await updateGameState('navigation', stripUndefined(nav));
  };

  // Append a single entry to the dead-reckoning log
  const addNavLogEntry = async (entry: NavLogEntry): Promise<void> => {
    await addNavLogEntries([entry]);
  };

  // Append a batch of entries in one write. Never loop over addNavLogEntry —
  // gameState is captured per render, so each await would re-read the same
  // stale navLog and the last write would clobber the rest.
  const addNavLogEntries = async (entries: NavLogEntry[]): Promise<void> => {
    if (!sessionId || entries.length === 0) return;
    const currentLog = gameState?.navLog || [];
    await updateGameState('navLog', [
      ...currentLog,
      ...entries.map((entry) => stripUndefined(entry))
    ]);
  };

  const contextValue: SessionContextType = {
    sessionId,
    userId,
    userName,
    setUserName,
    gameState,
    createSession,
    joinSession,
    leaveSession,
    updateGameState,
    activeUsers,
    isDM,
    canClaimDM,
    claimDMRole,
    releaseDMRole,
    addFleetShip,
    updateFleetShip,
    removeFleetShip,
    addPlayerFleetShip,
    updatePlayerFleetShip,
    removePlayerFleetShip,
    advanceDay,
    startVoyage,
    endVoyage,
    updateNavigation,
    addNavLogEntry,
    addNavLogEntries
  };

  return (
    <SessionContext.Provider value={contextValue}>
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = () => {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};